import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { animated, useTransition } from "@react-spring/web";
import useMeasure from "react-use-measure";
import { placementAlternateMap } from "./configs";
import "./index.less";

export type RefFunctions ={
  setVisible: (visible: boolean) => void;
}

type IPopoverPlaneProps = {
  className?: string;
  children: React.ReactNode;
  content: React.ReactNode;
  trigger?: "click" | "hover";
  placement?: "top" | "bottom" | "left" | "right" | "top-start" | "top-end" | "bottom-start" | "bottom-end";
  onRef?: (fns: RefFunctions) => void;
};

const PopoverPlane: React.FC<IPopoverPlaneProps> = (props) => {
  const { className, children, content, trigger = "click", placement = "bottom", onRef } = props;
  const [triggerRef, triggerOptions] = useMeasure();
  const [planeRef, planeOptions] = useMeasure();
  const [visible, setVisible] = useState(false);
  const thisTriggerRef = useRef<HTMLDivElement>(null);
  const thisPlaneRef = useRef<HTMLDivElement>(null);

  /** 根据视口宽度和两个options计算位置 */
  const planeStyle = useMemo(() => {
    const { width: triggerWidth, height: triggerHeight, top: triggerTop, left: triggerLeft } = triggerOptions;
    const { width: planeWidth, height: planeHeight } = planeOptions;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const placementMap: Record<string, any> = {};
    const leftAllow = triggerLeft >= planeWidth;
    const topAllow = triggerTop >= planeHeight;
    const rightAllow = viewportWidth - triggerLeft - triggerWidth >= planeWidth;
    const bottomAllow = viewportHeight - triggerTop - triggerHeight >= planeHeight;
    const leftContentAllow = triggerLeft + triggerWidth >= planeWidth;
    const rightContentAllow = viewportWidth - triggerLeft >= planeWidth;
    if (leftAllow) {
      // 左侧空间足够
      placementMap["left"] = { top: triggerTop + triggerHeight / 2 - planeHeight / 2, left: triggerLeft - planeWidth };
    }
    if (rightAllow) {
      // 右侧空间足够
      placementMap["right"] = { top: triggerTop + triggerHeight / 2 - planeHeight / 2, left: triggerLeft + triggerWidth };
    }
    if (topAllow) {
      // 上方空间足够
      placementMap["top"] = { top: triggerTop - planeHeight, left: triggerLeft + triggerWidth / 2 - planeWidth / 2 };
      // 左侧内容空间足够
      if (rightContentAllow) {
        placementMap["top-start"] = { top: triggerTop - planeHeight, left: triggerLeft };
      }
      // 右侧内容空间足够
      if (leftContentAllow) {
        placementMap["top-end"] = { top: triggerTop - planeHeight, left: triggerWidth + triggerLeft - planeWidth };
      }
    }
    if (bottomAllow) {
      // 下方空间足够
      placementMap["bottom"] = { top: triggerTop + triggerHeight, left: triggerLeft + triggerWidth / 2 - planeWidth / 2 };
      // 左侧内容空间足够
      if (rightContentAllow) {
        placementMap["bottom-start"] = { top: triggerTop + triggerHeight, left: triggerLeft };
      }
      // 右侧内容空间足够
      if (leftContentAllow) {
        placementMap["bottom-end"] = { top: triggerTop + triggerHeight, left: triggerWidth + triggerLeft - planeWidth };
      }
    }
    if (placementMap[placement]) {
      return placementMap[placement];
    } else {
      let newPlacement = placementAlternateMap[placement];
      if (!placementMap[newPlacement]) {
        newPlacement = Object.keys(placementMap)[0];
      }
      return placementMap[newPlacement];
    }
  }, [triggerOptions, planeOptions]);

  const transition = useTransition(visible, {
    from: { opacity: 0, translateY: 10 },
    enter: { opacity: 1, translateY: 0 },
    leave: { opacity: 0, translateY: 10 },
  });

  const eventProps = useMemo(() => {
    if (trigger === "click") {
      return {
        onClick: () => {
          setVisible((prev) => !prev);
        },
      };
    }
    if (trigger === "hover") {
      return {
        onMouseEnter: () => {
          setVisible(true);
        },
        onMouseLeave: () => {
          setVisible(false);
        },
      };
    }
    return {};
  }, [trigger]);

  useEffect(() => {
    if (trigger === "click") {
      document.addEventListener("click", (e) => {
        if (!thisPlaneRef.current?.contains(e.target as Node) && !thisTriggerRef.current?.contains(e.target as Node)) {
          setVisible(false);
        }
      });
    }
  }, [trigger]);

  useEffect(() => {
    onRef?.({
      setVisible,
    });
  }, [onRef]);

  return (
    <>
      <div
        className={className}
        ref={(ref) => {
          triggerRef(ref);
          thisTriggerRef.current = ref;
        }}
        {...eventProps}
      >
        {children}
      </div>
      {createPortal(
        transition((style, isOpen) => (
          <>
            {isOpen ? (
              <div
                className="popover-plane-wrapper"
                ref={(ref) => {
                  planeRef(ref);
                  thisPlaneRef.current = ref;
                }}
                style={planeStyle}
              >
                <animated.div style={style}>{content}</animated.div>
              </div>
            ) : null}
          </>
        )),
        document.body
      )}
    </>
  );
};

export default PopoverPlane;
